/**
 * A single player marker on the pitch: kit + event marker chips + number/name label.
 */

import React from 'react';
import { Text, View } from 'react-native';
import { SoccerKit } from '../../ui/SoccerKit';
import type { MatchHubLineupPlayer, MatchHubTeamSide } from '../../../src/match/types/matchHub.types';
import type { MatchHubLineupsStyles } from './MatchHubLineupsPanel.styles';

export const PITCH_KIT_SIZE = 36;
const PLAYER_STACK_W = 62;

function MarkerChips({
  markers,
  styles,
}: {
  markers: NonNullable<MatchHubLineupPlayer['markers']>;
  styles: MatchHubLineupsStyles;
}) {
  if (markers.length === 0) return null;
  return (
    <View style={styles.markerStrip}>
      {markers.map((m, i) => {
        if (m === 'goal') {
          return <Text key={`${m}-${i}`} style={[styles.markerChip, styles.markerGoal]}>G</Text>;
        }
        if (m === 'yellow' || m === 'red') {
          return (
            <Text key={`${m}-${i}`} style={[styles.markerChip, styles.markerCard]}>
              {m === 'yellow' ? 'Y' : 'R'}
            </Text>
          );
        }
        if (m === 'sub_on') {
          return <Text key={`${m}-${i}`} style={[styles.markerChip, styles.markerSub]}>↑</Text>;
        }
        if (m === 'sub_off') {
          return <Text key={`${m}-${i}`} style={[styles.markerChip, styles.markerSub]}>↓</Text>;
        }
        return null;
      })}
    </View>
  );
}

export interface PitchPlayerProps {
  player: MatchHubLineupPlayer;
  xPct: number;
  yPct: number;
  kit: MatchHubTeamSide['kit'];
  styles: MatchHubLineupsStyles;
}

export const PitchPlayer: React.FC<PitchPlayerProps> = ({ player, xPct, yPct, kit, styles }) => {
  const label = `${player.number} ${player.name}`;
  return (
    <View
      style={[
        styles.pitchPlayer,
        {
          left: `${xPct}%`,
          top: `${yPct}%`,
          width: PLAYER_STACK_W,
          transform: [{ translateX: -PLAYER_STACK_W / 2 }, { translateY: -PITCH_KIT_SIZE / 2 }],
        },
      ]}
      accessibilityLabel={label}
    >
      <View style={styles.pitchKitWrap}>
        <SoccerKit primary={kit.primary} secondary={kit.secondary} type={kit.type} size={PITCH_KIT_SIZE} />
        <MarkerChips markers={player.markers ?? []} styles={styles} />
      </View>
      <View style={styles.pitchPlayerLabelRow}>
        <Text style={styles.pitchPlayerNumInline} numberOfLines={1}>
          {player.number}
        </Text>
        <Text style={styles.pitchPlayerNameInline} numberOfLines={1}>
          {player.name}
        </Text>
      </View>
    </View>
  );
};
